import React, { useEffect, useState } from 'react';
import { Box, Stack, CircularProgress, List, ListItem, ListItemText, Chip, Typography } from '@mui/material';
import { PERMISSIONS } from '../../../constants/permissions';
import { DashboardService } from '../../../services/dashboard.service';
import { LeaveSummary, MonthlyLeavePoint, LeaveTypeUsage } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import HBars from '../charts/HBars';
import StackedColumns from '../charts/StackedColumns';
import StatTile from '../charts/StatTile';

const DASHBOARD_PERMISSION = PERMISSIONS.DASHBOARD_VIEW_MANAGER;
// HR sees institution-wide attendance/leave anyway; the summary endpoints only need DASHBOARD_VIEW
// server-side. Department heads hold the *_VIEW_ALL key but never DASHBOARD_VIEW, so stay out.
const HR_ACCESS = [[PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.LEAVE_APPLICATION_VIEW_ALL]];

const LeaveWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const [d, setD] = useState<LeaveSummary | null>(null);
    useEffect(() => {
        let live = true;
        DashboardService.getLeaveSummary(institutionId).then(x => { if (live) setD(x); }).catch(() => {});
        return () => { live = false; };
    }, [institutionId]);
    if (!d) return <CircularProgress size={20} />;
    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                <StatTile label="Pending" value={d.pending} tone={d.pending > 0 ? 'warning' : 'default'} />
                <StatTile label="Approved" value={d.approved} tone="good" />
                <StatTile label="Rejected" value={d.rejected} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>All applications to date</Typography>
            {d.byType.length > 0 && (
                <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Pending by type</Typography>
                    <HBars data={d.byType.map(t => ({ label: t.leaveType, value: t.count }))} colorIndex={3} />
                </>
            )}
            <List dense>
                {d.pendingQueue.slice(0, 5).map(r => (
                    <ListItem key={r.id} secondaryAction={r.daysOverdue > 0 ? <Chip size="small" color="error" label={`${r.daysOverdue}d overdue`} /> : null}>
                        <ListItemText primary={`${r.applicantName} — ${r.leaveType}`} secondary={`from ${r.startDate}`} />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

const loadLeaveTrend = (institutionId: string): Promise<MonthlyLeavePoint[]> =>
    DashboardService.getLeaveTrend(institutionId);

const LeaveTrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadLeaveTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // The backend zero-fills months, so an empty array means no window at all — an honest empty,
    // while a window of all-zero months is a real (quiet) chart, not an absence of data.
    if (d.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No leave applications in this window yet.
            </Typography>
        );
    }
    return (
        <StackedColumns
            data={d.map(p => ({ label: p.month, segments: [p.pending, p.approved, p.rejected, p.cancelled] }))}
            seriesLabels={['Pending', 'Approved', 'Rejected', 'Cancelled']}
        />
    );
};

const loadLeaveByType = (institutionId: string): Promise<LeaveTypeUsage[]> =>
    DashboardService.getLeaveByType(institutionId);

const LeaveByTypeWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadLeaveByType, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No approved leave for this calendar year yet.
            </Typography>
        );
    }
    return (
        <Box>
            <HBars
                data={d.map(t => ({
                    label: t.type,
                    value: t.applications,
                    tooltip: [
                        { label: 'Applications', value: String(t.applications) },
                        { label: 'Days', value: String(t.days) },
                    ],
                }))}
                colorIndex={3}
            />
            {/* The window is the whole calendar year, not year-to-date: leave approved now for a
                future date is already counted. The caption says so rather than reading as
                consumed-so-far. */}
            <Typography variant="caption" color="text.secondary">
                Approved · this calendar year, incl. upcoming
            </Typography>
        </Box>
    );
};

export const leaveWidgets: WidgetDef[] = [
    { id: 'leave.summary', module: 'leave', title: 'Leave — approvals & status', subtitle: 'Applications waiting for a decision and the oldest in the queue', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'md', component: LeaveWidget },
    { id: 'leave.trend', module: 'leave', title: 'Leave — applications by month', subtitle: 'Leave applications per month by outcome', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'lg', component: LeaveTrendWidget },
    { id: 'leave.byType', module: 'leave', title: 'Leave — usage by type', subtitle: 'Approved leave by type, this calendar year', permission: DASHBOARD_PERMISSION, alsoVisibleWith: HR_ACCESS, size: 'md', component: LeaveByTypeWidget },
];
